import React, { useEffect, useState } from 'react';
import { Alert, Card } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { useNotifications } from '../../../common/hooks/useNotifications';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  CreateUpdateProfileCategoryDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  convert,
  newProfileCategory,
  validate,
} from '../../../common/pokCore/contexts/ProfileCategoryContext';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { useNavigation } from '../../../common/navigation';

import { ProfileCategoryEditor } from './ProfileCategoryEditor';

type ParamTypes = {
  profileCategoryId: string;
};

export const ProfileCategoryView: React.FC = () => {
  const [profileCategory, setProfileCategory] =
    useState<CreateUpdateProfileCategoryDto>();

  const { profileCategoryId } = useParams<ParamTypes>() as {
    profileCategoryId?: string;
  };
  const [notSaved, setNotSaved] = useState(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const pok = usePokCore();
  const nav = useNavigation();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (!notSaved) {
      if (profileCategoryId) {
        pok.profileCategories
          .getById(profileCategoryId)
          .then(profileCategory => {
            setProfileCategory(convert(profileCategory));
          })
          .catch(errorResponse => {
            notifications.caughtError(errorResponse);
          });
      } else {
        setProfileCategory(newProfileCategory());
      }
    }
  }, [
    profileCategoryId,
    notifications,
    pok.profileCategories,
    refresh,
    notSaved,
  ]);

  const forceRefresh = () => {
    setRefresh(!refresh);
  };

  const editProfileCategory = async (
    profileCategoryCU: CreateUpdateProfileCategoryDto,
    id?: string,
  ) => {
    const status = await validate(profileCategoryCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.profileCategories.update(id, profileCategoryCU);
        } else {
          await pok.profileCategories.create(profileCategoryCU);
        }
        setNotSaved(false);
        forceRefresh();
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClose = () => {
    nav.profileCategories();
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {profileCategoryId === undefined &&
            t(tk.administration.profileCategoryAdd)}
          {profileCategoryId !== undefined &&
            t(tk.administration.profileCategory)}
          <b> {profileCategory?.name}</b>
        </Card.Title>
        <Alert show={notSaved} className="alert-unsaved">
          <b> {t(tk.common.unwrittenData)} </b>
        </Alert>
        {profileCategory && (
          <ProfileCategoryEditor
            id={profileCategoryId}
            profileCategory={profileCategory}
            onCompleted={editProfileCategory}
            closeAfterCompleted={profileCategoryId === undefined}
            handleClose={handleClose}
            notSaved={notSaved}
            setNotSaved={setNotSaved}
            readOnly={false}
          />
        )}
      </Card.Body>
    </Card>
  );
};
